import { defaultPlayersAbove } from "./band.ts";
import { decodeBase64, encodeBase64 } from "./base64.ts";
import { upsampleGrid } from "./grid.ts";
import { FACE_HEIGHT_MAX } from "../terrain/faces.ts";
import { EMPTY_TILE, MAX_TILE_ID, MAX_TILESET_INDEX, TILE_TURN, assertTilesetListFits, hasOrientationBits, unpackTile } from "./packed-tile.ts";
import {
  COLLISION_CELLS_PER_TILE,
  SCHEMA_VERSION,
  type CollisionLayer,
  type EntityInstance,
  type EntityLayer,
  type LayerDocument,
  type MapDocument,
  type PropBehaviour,
  type PropInstance,
  type PropLayer,
  type TerrainLayer,
  type TileLayer,
  type TileRef,
  type TilesetRef,
  type WorldDocument,
  type ZoneLayer,
} from "./types.ts";
import { plainInstanceFootprint, readInstanceFootprint } from "./footprint-cells.ts";
import { readIssueIgnores } from "./issue-ignores.ts";
import { validateDocument } from "./layout-rules.ts";
import { DIM_SCHEMA, plainMapLayout, plainSeams, plainWorldLayout, readMapLayout, readWorldLayout } from "./layout-file.ts";
import { readFrames } from "./tileset-frames.ts";
import { canonicalZoneLayer } from "./zones.ts";
import { plainDoorLayer, readDoorLayer } from "./door-read.ts";
import { assertUniqueDoorIds } from "./doors.ts";
import { asArray, asInteger, asIntegerInRange, asJsonRecord, asNumber, asPositiveInteger, asQuantised, asRecord, asString, asUint32, assertUniqueIds, sortedRecord } from "./validate.ts";

/**
 * Every schema this build reads, with the collision cells per tile along each axis its documents store:
 * schema 1 one, schema 2 two, schema 3 onwards the current resolution. Schema 4 adds a terrain layer's
 * `faceHeight` (ADR-072), which an older build must not drop on save; schema 5 the zone layer (ADR-054's
 * amendment of 2026-09-27), which an older build refuses by its kind, now naming the schema; schema 6 the
 * mirror bits of a packed tile (ADR-089), which an older build would read as a tileset index; schema 7 a
 * collision byte that may hold the game's open mask (ADR-017's amendment of 2026-10-04), which an older build
 * would OR into the grid as a bit that opens nothing; schema 8 a prop's sort offset in whole pixels under
 * `sortPixels` (ADR-098), which an older build would read as rows; schema 9 a placed prop's own `footprint`
 * (ADR-040 amendment of 2026-10-04), which an older build must not drop on save; schema 10 the world's layout,
 * a map's place, group and draft mark, the grid, the map groups and the seams (ADR-109 decision 10), which an
 * older build must not drop on save; schema 11 where a map sits and who shares it, a place's depth, a room's
 * host, its sharing, capacity and atmosphere (ADR-122 decision 1.5), which an older build would read as a plain
 * map and drop on save; schema 12 the door layer (ADR-122 decision 2.1), which an older build refuses by its kind and
 * which this build refuses by its kind in a document below 12; schema 13 a tileset entry's `frames` (ADR-129
 * decision 3.3), which an older build would drop on save, turning one pulled frame into its whole tileset, and
 * which this build refuses in a document below 13; schema 14 an atmosphere's `dim`, which a schema 13 build
 * refuses by name and which this build refuses in a document below 14. The layer's shape is the same from 6 on.
 */
const READABLE_SCHEMAS: Readonly<Record<number, number>> = { 1: 1, 2: 2, 3: COLLISION_CELLS_PER_TILE, 4: COLLISION_CELLS_PER_TILE, 5: COLLISION_CELLS_PER_TILE, 6: COLLISION_CELLS_PER_TILE, 7: COLLISION_CELLS_PER_TILE, 8: COLLISION_CELLS_PER_TILE, 9: COLLISION_CELLS_PER_TILE, 10: COLLISION_CELLS_PER_TILE, 11: COLLISION_CELLS_PER_TILE, 12: COLLISION_CELLS_PER_TILE, [SCHEMA_VERSION]: COLLISION_CELLS_PER_TILE, [DIM_SCHEMA]: COLLISION_CELLS_PER_TILE };

/**
 * The highest document schema `deserialiseWorld` accepts. A document above it is refused as newer; a carry-over or a
 * page judges what it can open against this, never against `SCHEMA_VERSION`, the lowest schema a document is written at.
 */
export const NEWEST_READ_SCHEMA = Math.max(...Object.keys(READABLE_SCHEMAS).map(Number));

/**
 * The schemas this build reads, in words: runs of consecutive numbers joined, "1 to 13".
 *
 * @returns The words.
 */
function readableSchemas(): string {
  const numbers = Object.keys(READABLE_SCHEMAS).map(Number).sort((a, b) => a - b);
  const runs: string[] = [];
  let start = 0;
  numbers.forEach((n, i) => {
    if (numbers[i + 1] !== n + 1) {
      runs.push(i === start ? `${n}` : `${numbers[start]} to ${n}`);
      start = i + 1;
    }
  });
  return runs.join(" and ");
}

/**
 * The first schema whose tile cells may carry mirror bits (ADR-089). A cell of an earlier schema reads
 * unchanged, since its tileset index never reached those bits; one that did is refused.
 */
const MIRROR_SCHEMA = 6;

/**
 * The first schema whose tileset entries may name frames (ADR-129 decision 3.3). An entry of an earlier schema
 * that holds `frames` is refused: no build that wrote that schema wrote the field.
 */
const FRAMES_SCHEMA = 13;

/**
 * The first schema whose props carry their sort offset in whole pixels, as `sortPixels` (ADR-098 decision 3).
 * A prop of an earlier schema carries whole rows as `sortOffset`, converted on load at the world's tile size.
 */
const SORT_PIXELS_SCHEMA = 8;

/**
 * What the schema a document was stored at says about its content: the schema itself, which names the unit
 * of a prop's sort offset, the factor its collision cells are upsampled by, and whether its tile cells may
 * carry mirror bits.
 */
interface StoredSchema {
  readonly schemaVersion: number;
  readonly collisionScale: number;
  readonly mirrors: boolean;
}

/**
 * JSON serialisation of authoring documents. Output is deterministic: keys are written in a fixed order and
 * typed arrays are base64 strings, so equal documents serialise to identical bytes. Loading validates the
 * structure and reports the path of the first problem.
 *
 * Loading throws rather than returning a `Result` because documents are produced by this module's own
 * serialiser: a document that fails to load is corrupt or tampered with, not a routine outcome, and the
 * dev server converts the throw into a response at its boundary.
 */

const INDENT = 2;
const BYTES_PER_U32 = 4;

/**
 * The lowest schema that can hold a world: `DIM_SCHEMA` when any map's atmosphere carries a `dim`, which a build of
 * schema 13 refuses by name, and `SCHEMA_VERSION` otherwise, so a world that uses nothing newer stays readable by
 * the build before. Later steps that add an optional name follow the same rule.
 *
 * @param world The world.
 * @returns The schema number to write.
 */
export function documentSchemaOf(world: WorldDocument): number {
  return world.maps.some((map) => map.atmosphere?.dim !== undefined) ? DIM_SCHEMA : SCHEMA_VERSION;
}

/**
 * Serialises a world to JSON text at the lowest schema that can hold it (`documentSchemaOf`): `SCHEMA_VERSION`, or
 * `DIM_SCHEMA` when a map's atmosphere carries a `dim`, whatever schema the document was read at.
 *
 * @param world Document to serialise.
 * @returns Pretty-printed JSON with deterministic key order.
 */
export function serialiseWorld(world: WorldDocument): string {
  const plain = {
    schemaVersion: documentSchemaOf(world),
    id: world.id,
    name: world.name,
    tileSize: world.tileSize,
    chunkSize: world.chunkSize,
    tilesets: world.tilesets.map((tileset) => (tileset.frames === undefined ? { id: tileset.id } : { id: tileset.id, frames: [...tileset.frames] })),
    ...(world.terrainNames === undefined || Object.keys(world.terrainNames).length === 0 ? {} : { terrainNames: Object.fromEntries(Object.entries(world.terrainNames).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) }),
    ...plainWorldLayout(world),
    maps: world.maps.map(serialiseMap),
    ...plainSeams(world),
  };
  return JSON.stringify(plain, null, INDENT);
}

/**
 * Converts a map to its plain JSON shape.
 *
 * @param map Map document.
 * @returns Plain object with fixed key order.
 */
function serialiseMap(map: MapDocument): Record<string, unknown> {
  return {
    id: map.id,
    name: map.name,
    width: map.width,
    height: map.height,
    seed: map.seed,
    ...plainMapLayout(map),
    ...(map.playersAbove !== undefined ? { playersAbove: map.playersAbove } : {}),
    ...(map.groups !== undefined && map.groups.length > 0 ? { groups: map.groups.map((g) => ({ id: g.id, name: g.name })) } : {}),
    layers: map.layers.map((layer) => serialiseLayer(layer, map.id)),
    properties: sortedRecord(map.properties, `maps.${map.id}.properties`),
    ...(map.ignoredIssues !== undefined && Object.keys(map.ignoredIssues).length > 0 ? { ignoredIssues: readIssueIgnores(map.ignoredIssues, `maps.${map.id}.ignoredIssues`) } : {}),
  };
}

/**
 * The plain shape of a layer's group membership: present only when set.
 *
 * @param layer The layer.
 * @returns `{ group }` or nothing.
 */
function groupOf(layer: LayerDocument): { group?: string } {
  return layer.group === undefined ? {} : { group: layer.group };
}

/**
 * Converts a layer to its plain JSON shape, encoding typed arrays as base64. A prop that holds both a behaviour
 * and a footprint of its own is refused, since the reader refuses it too (ADR-040 amendment of 2026-10-04): no
 * writer can save a world that will not load.
 *
 * @param layer Layer document.
 * @param mapId The layer's map, for an error's path.
 * @returns Plain object with fixed key order.
 * @throws Error naming the prop's path when it holds a behaviour and its own footprint.
 */
function serialiseLayer(layer: LayerDocument, mapId: string): Record<string, unknown> {
  if (layer.kind === "prop") {
    const both = layer.props.find((prop) => prop.behaviour !== undefined && prop.footprint !== undefined);
    if (both !== undefined) {
      throw new Error(`maps.${mapId}.layers.${layer.id}.props.${both.id}.footprint: a prop with a behaviour takes no footprint of its own`);
    }
  }
  switch (layer.kind) {
    case "terrain":
      return { kind: layer.kind, id: layer.id, name: layer.name, ...(layer.autoName === undefined ? {} : { autoName: layer.autoName }), ...groupOf(layer), terrain: layer.terrain, variant: layer.variant, ...(layer.faceHeight === undefined ? {} : { faceHeight: layer.faceHeight }), corners: encodeBase64(layer.corners) };
    case "tile":
      return { kind: layer.kind, id: layer.id, name: layer.name, ...groupOf(layer), tiles: encodeBase64(layer.tiles) };
    case "collision":
      return { kind: layer.kind, id: layer.id, name: layer.name, ...groupOf(layer), cells: encodeBase64(layer.cells) };
    case "zone": {
      const canonical = canonicalZoneLayer(layer);
      return { kind: layer.kind, id: layer.id, name: layer.name, ...groupOf(layer), types: [...canonical.types], cells: encodeBase64(canonical.cells) };
    }
    case "prop":
      return { kind: layer.kind, id: layer.id, name: layer.name, ...groupOf(layer), props: layer.props.map((p) => ({ id: p.id, frame: { tileset: p.frame.tileset, tile: p.frame.tile }, x: p.x, y: p.y, flipX: p.flipX, flipY: p.flipY, sortPixels: p.sortPixels, ...(p.behaviour === undefined ? {} : { behaviour: plainBehaviour(p.behaviour, `props.${p.id}.behaviour`) }), ...(p.keepPlacement === true ? { keepPlacement: true } : {}), ...(p.footprint === undefined ? {} : { footprint: plainInstanceFootprint(p.footprint) }) })) };
    case "entity":
      return { kind: layer.kind, id: layer.id, name: layer.name, ...groupOf(layer), entities: layer.entities.map((e) => ({ id: e.id, type: e.type, x: e.x, y: e.y, fields: sortedRecord(e.fields, `entities.${e.id}.fields`) })) };
    case "door":
      return plainDoorLayer(layer, groupOf(layer));
  }
}

/**
 * A document of a schema this build does not read. `found` is the document's version and `reads` the highest
 * this build reads; `newer` says the document was written by a later build, which an open tab meets when the
 * editor was updated under it, as against a number that was never a schema.
 */
export class UnsupportedSchemaError extends Error {
  /** Whether the document's schema is above every one this build reads. */
  public readonly newer: boolean;

  /**
   * @param found The document's schema version.
   * @param reads The highest version this build reads.
   * @param readable The versions this build reads, in words, such as "1 to 13".
   */
  public constructor(
    public readonly found: number,
    public readonly reads: number,
    readable: string = `1 to ${reads}`,
  ) {
    super(`schemaVersion ${found} is not supported; this build reads ${readable}`);
    this.name = "UnsupportedSchemaError";
    this.newer = found > reads;
  }
}

/**
 * Parses and validates JSON text produced by `serialiseWorld`.
 *
 * @param text JSON text.
 * @returns The world document, with typed arrays restored.
 * Reads every schema of `READABLE_SCHEMAS`: schemas 1 and 2, whose collision layers held fewer cells per
 * tile (one and two along each axis), are expanded to the current resolution on load (ADR-040); schema 3
 * reads as it is, lacking only face heights; a prop's sort offset of a schema before 7, whole rows, becomes
 * whole pixels at the world's tile size (ADR-098); the layout names of schema 10 and the room names of schema 11 are
 * refused by name in an earlier document, and the layout read is checked by `validateDocument` (ADR-109 decision 10). The result
 * carries `documentSchemaOf` of what was read.
 *
 * @throws SyntaxError when the text is not JSON; UnsupportedSchemaError when the schema version is not one this
 *   build reads; Error naming the offending path when the structure is invalid, or the map, group or seam
 *   when the layout breaks a rule.
 */
export function deserialiseWorld(text: string): WorldDocument {
  const root = JSON.parse(text) as unknown;
  const world = asRecord(root, "world");
  const schemaVersion = asNumber(world["schemaVersion"], "schemaVersion");
  const storedCells = READABLE_SCHEMAS[schemaVersion];
  if (storedCells === undefined) {
    throw new UnsupportedSchemaError(schemaVersion, NEWEST_READ_SCHEMA, readableSchemas());
  }
  const collisionScale = COLLISION_CELLS_PER_TILE / storedCells;
  const tilesets = asArray(world["tilesets"], "tilesets").map((item, i) => readTileset(item, schemaVersion, `tilesets[${i}]`));
  assertTilesetListFits(tilesets.length, "tilesets has");
  assertUniqueIds(tilesets.map((t) => t.id), "tilesets");
  const tileSize = asPositiveInteger(world["tileSize"], "tileSize");
  const layout = readWorldLayout(world, schemaVersion);
  const maps = asArray(world["maps"], "maps").map((item, i) => readMap(item, tilesets, tileSize, { schemaVersion, collisionScale, mirrors: schemaVersion >= MIRROR_SCHEMA }, `maps[${i}]`));
  assertUniqueIds(maps.map((m) => m.id), "maps");
  const terrainNames = world["terrainNames"] === undefined ? {} : readTerrainNames(world["terrainNames"], "terrainNames");
  const read: WorldDocument = {
    schemaVersion: SCHEMA_VERSION,
    id: asString(world["id"], "id"),
    name: asString(world["name"], "name"),
    tileSize,
    chunkSize: asPositiveInteger(world["chunkSize"], "chunkSize"),
    tilesets,
    ...(Object.keys(terrainNames).length === 0 ? {} : { terrainNames }),
    ...(layout.grid === undefined ? {} : { grid: layout.grid }),
    ...(layout.mapGroups === undefined ? {} : { mapGroups: layout.mapGroups }),
    maps,
    ...(layout.seams === undefined ? {} : { seams: layout.seams }),
  };
  validateDocument(read);
  return { ...read, schemaVersion: documentSchemaOf(read) };
}

/**
 * Reads the author's names for terrain entries: an object of entry key to a non-blank name.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The names.
 * @throws Error when the value is not an object of non-blank strings or a key is `__proto__`.
 */
function readTerrainNames(value: unknown, path: string): { readonly [entry: string]: string } {
  const record = asRecord(value, path);
  const names: Record<string, string> = {};
  for (const [entry, name] of Object.entries(record)) {
    if (entry === "__proto__") {
      throw new Error(`${path} has a __proto__ key`);
    }
    const text = asString(name, `${path}.${entry}`);
    if (text.trim() === "") {
      throw new Error(`${path}.${entry} is blank`);
    }
    names[entry] = text;
  }
  return names;
}

/**
 * Reads a tileset reference: its id and, from `FRAMES_SCHEMA` on, the frames a partial entry names. The list's
 * shape is all that is checked (ADR-129 decision 3.3): no cap, and nothing a map places against it. Any other
 * field is ignored.
 *
 * @param value Candidate value.
 * @param schemaVersion The schema the document was stored at.
 * @param path Path for error messages.
 * @returns The tileset reference.
 * @throws Error naming the path when the id is not a string, `frames` stands in a document below
 *   `FRAMES_SCHEMA`, or the list is not a list, is empty, holds anything but whole numbers from 0 to
 *   `MAX_TILE_ID`, or is unsorted or repeated.
 */
function readTileset(value: unknown, schemaVersion: number, path: string): TilesetRef {
  const record = asRecord(value, path);
  const id = asString(record["id"], `${path}.id`);
  if (record["frames"] === undefined) {
    return { id };
  }
  if (schemaVersion < FRAMES_SCHEMA) {
    throw new Error(`${path}.frames is not a field of a schema ${schemaVersion} tileset; it came in schema ${FRAMES_SCHEMA}`);
  }
  return { id, frames: readFrames(asArray(record["frames"], `${path}.frames`), `${path}.frames`) };
}

/**
 * Reads a map, validating each layer against the map's dimensions and the world's tilesets.
 *
 * @param value Candidate value.
 * @param tilesets The world's tilesets, for packed tile validation.
 * @param tileSize Tile size in pixels; prop positions are multiples of `1/tileSize`.
 * @param stored What the stored schema says about the grids.
 * @param path Path for error messages.
 * @returns The map document.
 */
function readMap(value: unknown, tilesets: readonly TilesetRef[], tileSize: number, stored: StoredSchema, path: string): MapDocument {
  const record = asRecord(value, path);
  const width = asPositiveInteger(record["width"], `${path}.width`);
  const height = asPositiveInteger(record["height"], `${path}.height`);
  const layers = asArray(record["layers"], `${path}.layers`).map((item, i) => readLayer(item, width, height, tilesets, tileSize, stored, `${path}.layers[${i}]`));
  assertUniqueIds(layers.map((l) => l.id), `${path}.layers`);
  assertUniqueDoorIds({ layers }, path);
  const groups = record["groups"] === undefined ? [] : asArray(record["groups"], `${path}.groups`).map((item, i) => {
    const group = asRecord(item, `${path}.groups[${i}]`);
    return { id: asString(group["id"], `${path}.groups[${i}].id`), name: asString(group["name"], `${path}.groups[${i}].name`) };
  });
  assertUniqueIds(groups.map((g) => g.id), `${path}.groups`);
  const groupIds = new Set(groups.map((g) => g.id));
  layers.forEach((layer, i) => {
    if (layer.group !== undefined && !groupIds.has(layer.group)) {
      throw new Error(`${path}.layers[${i}].group names group ${layer.group}, which the map lacks`);
    }
  });
  const band = record["playersAbove"] === undefined ? defaultPlayersAbove(layers) : asUint32(record["playersAbove"], `${path}.playersAbove`);
  if (band > layers.length) {
    throw new Error(`${path}.playersAbove is ${band}, beyond the ${layers.length} layers`);
  }
  const ignored = record["ignoredIssues"] === undefined ? {} : readIssueIgnores(record["ignoredIssues"], `${path}.ignoredIssues`);
  return {
    id: asString(record["id"], `${path}.id`),
    name: asString(record["name"], `${path}.name`),
    width,
    height,
    seed: asUint32(record["seed"], `${path}.seed`),
    ...readMapLayout(record, stored.schemaVersion, path),
    playersAbove: band,
    ...(groups.length > 0 ? { groups } : {}),
    layers,
    properties: asJsonRecord(record["properties"], `${path}.properties`),
    ...(Object.keys(ignored).length > 0 ? { ignoredIssues: ignored } : {}),
  };
}

/**
 * Reads a terrain layer's face height (ADR-072).
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The height.
 * @throws Error naming the path when the value is not an integer from 1 to `FACE_HEIGHT_MAX`.
 */
function readFaceHeight(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 1 || value > FACE_HEIGHT_MAX) {
    throw new Error(`${path} must be an integer from 1 to ${FACE_HEIGHT_MAX}`);
  }
  return value;
}

/**
 * Reads one layer of any kind.
 *
 * @param value Candidate value.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param tilesets The world's tilesets.
 * @param tileSize Tile size in pixels; prop positions are multiples of `1/tileSize`.
 * @param stored What the stored schema says about the grids.
 * @param path Path for error messages.
 * @returns The layer document.
 * @throws Error when the kind is unknown or a grid has the wrong length.
 */
function readLayer(value: unknown, width: number, height: number, tilesets: readonly TilesetRef[], tileSize: number, stored: StoredSchema, path: string): LayerDocument {
  const record = asRecord(value, path);
  const kind = asString(record["kind"], `${path}.kind`);
  const id = asString(record["id"], `${path}.id`);
  const name = asString(record["name"], `${path}.name`);
  const membership = record["group"] === undefined ? {} : { group: asString(record["group"], `${path}.group`) };
  switch (kind) {
    case "terrain": {
      const corners = decodeBase64(asString(record["corners"], `${path}.corners`));
      const expected = (width + 1) * (height + 1);
      if (corners.length !== expected) {
        throw new Error(`${path}.corners has ${corners.length} vertices, expected ${expected}`);
      }
      const autoName = record["autoName"] === undefined ? {} : { autoName: asBoolean(record["autoName"], `${path}.autoName`) };
      const faceHeight = record["faceHeight"] === undefined ? {} : { faceHeight: readFaceHeight(record["faceHeight"], `${path}.faceHeight`) };
      const layer: TerrainLayer = { kind, id, name, ...autoName, ...membership, terrain: asString(record["terrain"], `${path}.terrain`), variant: asString(record["variant"], `${path}.variant`), ...faceHeight, corners };
      return layer;
    }
    case "collision": {
      const bytes = decodeBase64(asString(record["cells"], `${path}.cells`));
      const { collisionScale } = stored;
      const storedPerTile = COLLISION_CELLS_PER_TILE / collisionScale;
      const expected = width * height * storedPerTile * storedPerTile;
      if (bytes.length !== expected) {
        throw new Error(`${path}.cells has ${bytes.length} cells, expected ${expected}`);
      }
      const cells = collisionScale === 1 ? bytes : upsampleGrid(bytes, width * storedPerTile, height * storedPerTile, collisionScale);
      const layer: CollisionLayer = { kind, id, name, ...membership, cells };
      return layer;
    }
    case "zone":
      return readZoneLayer(record, id, name, membership, width, height, path);
    case "door":
      return readDoorLayer(record, { id, name, membership }, { width, height, schemaVersion: stored.schemaVersion }, path);
    case "tile": {
      const bytes = decodeBase64(asString(record["tiles"], `${path}.tiles`));
      if (bytes.length !== width * height * BYTES_PER_U32) {
        throw new Error(`${path}.tiles has ${bytes.length / BYTES_PER_U32} cells, expected ${width * height}`);
      }
      const tiles = readU32Array(bytes);
      tiles.forEach((packed, i) => {
        if (packed === EMPTY_TILE) {
          return;
        }
        if (!stored.mirrors && hasOrientationBits(packed)) {
          throw new Error(`${path}.tiles[${i}] uses tileset index ${packed >>> 16}, beyond the ${MAX_TILESET_INDEX + 1} tilesets a document before schema ${MIRROR_SCHEMA} can carry into schema ${SCHEMA_VERSION}`);
        }
        if ((packed & TILE_TURN) !== 0) {
          throw new Error(`${path}.tiles[${i}] carries a quarter turn, which this build does not read`);
        }
        const { tilesetIndex } = unpackTile(packed);
        if (tilesetIndex >= tilesets.length) {
          throw new Error(`${path}.tiles[${i}] refers to tileset index ${tilesetIndex}, beyond ${tilesets.length} tilesets`);
        }
      });
      const layer: TileLayer = { kind, id, name, ...membership, tiles };
      return layer;
    }
    case "prop": {
      const layer: PropLayer = { kind, id, name, ...membership, props: asArray(record["props"], `${path}.props`).map((item, i) => readProp(item, tilesets, tileSize, stored.schemaVersion, `${path}.props[${i}]`)) };
      return layer;
    }
    case "entity": {
      const layer: EntityLayer = { kind, id, name, ...membership, entities: asArray(record["entities"], `${path}.entities`).map((item, i) => readEntity(item, `${path}.entities[${i}]`)) };
      return layer;
    }
    default:
      throw new Error(`${path}.kind ${JSON.stringify(kind)} is not a layer kind`);
  }
}

/**
 * Reads a zone layer (ADR-054's amendment of 2026-09-27): its table of distinct, non-blank type ids and one
 * byte per collision cell, each 0 or a 1-based index into the table, returned canonical.
 *
 * @param record The layer's record.
 * @param id The layer's id.
 * @param name The layer's name.
 * @param membership The layer's group, when it has one.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param path Path for error messages.
 * @returns The layer.
 * @throws Error naming the path when a type id is blank or repeated, the grid has the wrong length or a cell
 *   indexes beyond the table.
 */
function readZoneLayer(record: Record<string, unknown>, id: string, name: string, membership: { group?: string }, width: number, height: number, path: string): ZoneLayer {
  const types = asArray(record["types"], `${path}.types`).map((item, i) => {
    const type = asString(item, `${path}.types[${i}]`);
    if (type === "") {
      throw new Error(`${path}.types[${i}] is blank`);
    }
    return type;
  });
  assertUniqueIds(types, `${path}.types`);
  const cells = decodeBase64(asString(record["cells"], `${path}.cells`));
  const expected = width * height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
  if (cells.length !== expected) {
    throw new Error(`${path}.cells has ${cells.length} cells, expected ${expected}`);
  }
  cells.forEach((value, i) => {
    if (value > types.length) {
      throw new Error(`${path}.cells[${i}] holds ${value}, beyond the layer's ${types.length} types`);
    }
  });
  return canonicalZoneLayer({ kind: "zone", id, name, ...membership, types, cells });
}

/**
 * Decodes little-endian bytes into a `Uint32Array` without depending on host endianness.
 *
 * @param bytes Byte length must be a multiple of four.
 * @returns The decoded words.
 */
function readU32Array(bytes: Uint8Array): Uint32Array {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const words = new Uint32Array(bytes.length / BYTES_PER_U32);
  for (let i = 0; i < words.length; i += 1) {
    words[i] = view.getUint32(i * BYTES_PER_U32, true);
  }
  return words;
}

/**
 * Reads a prop instance, checking its frame against the world's tilesets. `flipX`, `flipY` and the sort offset
 * default to false and 0 when absent (ADR-022) so earlier documents load, and `behaviour` is absent on every
 * prop saved before ADR-053. The sort offset is whole pixels under `sortPixels` from schema 8 on, and whole
 * rows under `sortOffset` before it, read as rows times the tile size (ADR-098 decision 3); either name in a
 * document of the other's schema is refused, so a row count is never taken for pixels. `keepPlacement` is read as a boolean and kept
 * only when true (ADR-040 amendment of 2026-10-04), so absent and false are one document. A prop's own `footprint`
 * (ADR-040 amendment of 2026-10-04) is read into canonical order and refused beside a behaviour.
 *
 * @param value Candidate value.
 * @param tilesets The world's tilesets.
 * @param tileSize Tile size in pixels; prop positions are multiples of `1/tileSize`.
 * @param schemaVersion The schema the document was stored at.
 * @param path Path for error messages.
 * @returns The prop instance.
 * @throws Error naming the path when a field is malformed or the sort offset is under the other schema's name.
 */
function readProp(value: unknown, tilesets: readonly TilesetRef[], tileSize: number, schemaVersion: number, path: string): PropInstance {
  const record = asRecord(value, path);
  if (record["footprint"] !== undefined && record["behaviour"] !== undefined) {
    throw new Error(`${path}.footprint: a prop with a behaviour takes no footprint of its own`);
  }
  const inPixels = schemaVersion >= SORT_PIXELS_SCHEMA;
  const [read, other] = inPixels ? ["sortPixels", "sortOffset"] : ["sortOffset", "sortPixels"];
  if (record[other] !== undefined) {
    throw new Error(`${path}.${other} is not a field of a schema ${schemaVersion} prop, whose sort offset is ${read}, in whole ${inPixels ? "pixels" : "rows"}`);
  }
  const stored = record[read] === undefined ? 0 : asInteger(record[read], `${path}.${read}`);
  return {
    id: asString(record["id"], `${path}.id`),
    frame: readTileRef(record["frame"], tilesets, `${path}.frame`),
    x: asQuantised(record["x"], tileSize, `${path}.x`),
    y: asQuantised(record["y"], tileSize, `${path}.y`),
    flipX: record["flipX"] === undefined ? false : asBoolean(record["flipX"], `${path}.flipX`),
    flipY: record["flipY"] === undefined ? false : asBoolean(record["flipY"], `${path}.flipY`),
    sortPixels: inPixels ? stored : stored * tileSize,
    ...(record["behaviour"] === undefined ? {} : { behaviour: readBehaviour(record["behaviour"], tilesets, `${path}.behaviour`) }),
    ...(record["footprint"] === undefined ? {} : { footprint: readInstanceFootprint(record["footprint"], `${path}.footprint`) }),
    ...(record["keepPlacement"] !== undefined && asBoolean(record["keepPlacement"], `${path}.keepPlacement`) ? { keepPlacement: true as const } : {}),
  };
}

/**
 * Tile references keyed by name, sorted by key with fixed keys per reference.
 *
 * @param refs The references.
 * @returns The plain object.
 */
function sortedTileRefs(refs: { readonly [key: string]: TileRef }): Record<string, unknown> {
  return Object.fromEntries([...Object.keys(refs)].sort().map((id) => [id, { tileset: refs[id]?.tileset, tile: refs[id]?.tile }]));
}

/**
 * A prop's behaviour with a fixed key order, its fields, state frames and transition animations sorted by
 * key, so equal documents serialise to identical bytes (ADR-053, ADR-058).
 *
 * @param behaviour The behaviour.
 * @param path Path for error messages.
 * @returns The plain object.
 * @throws Error when a field name is forbidden.
 */
function plainBehaviour(behaviour: PropBehaviour, path: string): Record<string, unknown> {
  return {
    type: behaviour.type,
    fields: sortedRecord(behaviour.fields, `${path}.fields`),
    ...(behaviour.frames === undefined ? {} : { frames: sortedTileRefs(behaviour.frames) }),
    ...(behaviour.animations === undefined ? {} : { animations: sortedTileRefs(behaviour.animations) }),
    ...(behaviour.activation === undefined ? {} : { activation: behaviour.activation.map((cell) => [cell[0], cell[1]]) }),
  };
}

/**
 * Reads tile references keyed by name, each checked against the world's tilesets.
 *
 * @param value Candidate value.
 * @param tilesets The world's tilesets.
 * @param path Path for error messages.
 * @returns The references.
 * @throws Error naming the path when the value is not an object or a reference is malformed.
 */
function readTileRefs(value: unknown, tilesets: readonly TilesetRef[], path: string): Record<string, TileRef> {
  const record = asRecord(value, path);
  return Object.fromEntries(Object.keys(record).map((id) => [id, readTileRef(record[id], tilesets, `${path}.${id}`)]));
}

/**
 * Reads a prop's behaviour (ADR-053), checking every state frame and transition animation (ADR-058) against
 * the world's tilesets and every activation cell as an integer `[column, rowsUp]` pair. Cells may be
 * negative: activation cells reach outside the frame.
 *
 * @param value Candidate value.
 * @param tilesets The world's tilesets.
 * @param path Path for error messages.
 * @returns The behaviour.
 * @throws Error naming the path when the type, fields, a frame, an animation or a cell is malformed.
 */
function readBehaviour(value: unknown, tilesets: readonly TilesetRef[], path: string): PropBehaviour {
  const record = asRecord(value, path);
  const frames = record["frames"] === undefined ? undefined : readTileRefs(record["frames"], tilesets, `${path}.frames`);
  const animations = record["animations"] === undefined ? undefined : readTileRefs(record["animations"], tilesets, `${path}.animations`);
  const activation = record["activation"] === undefined ? undefined : asArray(record["activation"], `${path}.activation`).map((cell, i) => readCell(cell, `${path}.activation[${i}]`));
  return {
    type: asString(record["type"], `${path}.type`),
    fields: asJsonRecord(record["fields"], `${path}.fields`),
    ...(frames === undefined ? {} : { frames }),
    ...(animations === undefined ? {} : { animations }),
    ...(activation === undefined ? {} : { activation }),
  };
}

/**
 * Reads one `[column, rowsUp]` cell.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The cell.
 * @throws Error when the value is not a pair of integers.
 */
function readCell(value: unknown, path: string): readonly [number, number] {
  const pair = asArray(value, path);
  if (pair.length !== 2) {
    throw new Error(`${path} must be a [column, rowsUp] pair`);
  }
  return [asInteger(pair[0], `${path}[0]`), asInteger(pair[1], `${path}[1]`)];
}

/**
 * Reads a boolean.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The boolean.
 * @throws Error when the value is not a boolean.
 */
function asBoolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`${path} must be a boolean`);
  }
  return value;
}

/**
 * Reads a tile reference, checking the tileset exists in the world and the tile ID is in range.
 *
 * @param value Candidate value.
 * @param tilesets The world's tilesets.
 * @param path Path for error messages.
 * @returns The tile reference.
 * @throws Error when the tileset is unknown or the tile ID is out of range.
 */
function readTileRef(value: unknown, tilesets: readonly TilesetRef[], path: string): TileRef {
  const record = asRecord(value, path);
  const tileset = asString(record["tileset"], `${path}.tileset`);
  if (!tilesets.some((t) => t.id === tileset)) {
    throw new Error(`${path}.tileset ${JSON.stringify(tileset)} is not in the world's tilesets`);
  }
  return { tileset, tile: asIntegerInRange(record["tile"], MAX_TILE_ID, `${path}.tile`) };
}

/**
 * Reads an entity instance.
 *
 * @param value Candidate value.
 * @param path Path for error messages.
 * @returns The entity instance.
 */
function readEntity(value: unknown, path: string): EntityInstance {
  const record = asRecord(value, path);
  return {
    id: asString(record["id"], `${path}.id`),
    type: asString(record["type"], `${path}.type`),
    x: asNumber(record["x"], `${path}.x`),
    y: asNumber(record["y"], `${path}.y`),
    fields: asJsonRecord(record["fields"], `${path}.fields`),
  };
}
